import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  FiUsers, FiTrendingUp, FiTrendingDown, FiRefreshCw, FiDownload,
  FiArrowUpRight, FiBriefcase, FiCalendar, FiCheckCircle, FiCreditCard
} from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';

const BRAND = { 100: '#F1E6F5', 200: '#E3CDEB', 300: '#CFAEDB', 400: '#B082BE', 600: '#8B579C', 800: '#5D396A' };
const NEUTRAL = { 200: '#E5E7ED', 400: '#9BA3B4', 500: '#6B7488', 900: '#12213D' };

const STATUS_STYLES = {
  completed: { badge: 'bg-green-50 text-green-700', dot: 'bg-green-500', color: '#22C55E' },
  confirmed: { badge: 'bg-brand-100 text-brand-800', dot: 'bg-brand-500', color: BRAND[400] },
  pending: { badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', color: '#F59E0B' },
  cancelled: { badge: 'bg-red-50 text-red-700', dot: 'bg-red-500', color: '#EF4444' },
};
const statusStyle = (status) => STATUS_STYLES[status] || { badge: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400', color: NEUTRAL[400] };

const PERIODS = [
  { value: '7', label: '7D' },
  { value: '30', label: '30D' },
  { value: '90', label: '90D' },
  { value: '365', label: '1Y' },
];

const tooltipStyle = {
  backgroundColor: '#fff',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(18, 33, 61, 0.12)',
  fontSize: 12,
  padding: '8px 12px',
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
}).format(amount || 0);

const formatNumber = (num) => new Intl.NumberFormat('en-IN').format(num || 0);

const formatCompactCurrency = (value) => {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
  return `₹${value}`;
};

const percent = (part, total) => (total ? ((part / total) * 100).toFixed(1) : '0.0');

const monthLabel = (value) => {
  const [year, month] = value.split('-');
  return new Date(Number(year), Number(month) - 1).toLocaleString('en-IN', { month: 'short' });
};

const shortDate = (value) => new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

const Card = ({ className = '', children }) => (
  <div className={`card p-5 ${className}`}>{children}</div>
);

const CardHeader = ({ icon: Icon, title, subtitle, to, children }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div className="flex items-center gap-3 min-w-0">
      {Icon && (
        <span className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
          <Icon size={17} />
        </span>
      )}
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold text-gray-900 truncate">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {children}
      {to && (
        <Link to={to} className="icon-btn" aria-label={`Open ${title}`}>
          <FiArrowUpRight size={17} />
        </Link>
      )}
    </div>
  </div>
);

const GrowthPill = ({ growth, light }) => {
  if (growth === undefined || growth === null) return null;
  const up = growth >= 0;
  const Icon = up ? FiTrendingUp : FiTrendingDown;
  const tone = light
    ? 'bg-white/20 text-white'
    : up ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>
      <Icon size={13} />
      {up ? '+' : '-'}{Math.abs(growth).toFixed(1)}%
    </span>
  );
};

const StatTile = ({ icon: Icon, label, value, hint }) => (
  <Card className="flex items-center gap-4">
    <span className="w-11 h-11 rounded-2xl bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
      <Icon size={19} />
    </span>
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-semibold text-gray-900 leading-tight mt-0.5">{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-0.5 truncate">{hint}</p>}
    </div>
  </Card>
);

const EmptyChart = ({ height }) => (
  <div className="flex items-center justify-center text-sm text-gray-400" style={{ height }}>
    No data for this period
  </div>
);

const AdminDashboard = () => {
  const { adminUser } = useStateContext();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

  const fetchDashboardData = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    else setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/admin/dashboard-stats?period=${selectedPeriod}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const exportData = () => {
    if (!dashboardData) return;

    const exportContent = {
      period: `${selectedPeriod} days`,
      exportDate: new Date().toISOString(),
      totalStats: dashboardData.totalStats,
      revenueStats: dashboardData.revenueStats,
      bookingStats: dashboardData.bookingStats,
      vendorStats: dashboardData.vendorStats
    };

    const blob = new Blob([JSON.stringify(exportContent, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_data_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const firstName = adminUser?.name?.split(' ')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-brand-200 border-t-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="card p-10 text-center max-w-md mx-auto mt-16">
        <p className="text-gray-900 font-medium">Couldn't load the dashboard</p>
        <p className="text-sm text-gray-500 mt-1">Check your connection and try again.</p>
        <button
          onClick={() => fetchDashboardData()}
          className="mt-5 px-5 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-full hover:bg-brand-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const { totalStats, revenueStats, bookingStats, vendorStats, recentBookings = [], topVendors = [],
          monthlyRevenue = [], bookingTrends = [], paymentMethodStats = [], statusDistribution = [] } = dashboardData;

  const peakMonthRevenue = Math.max(0, ...monthlyRevenue.map((m) => m.revenue || 0));
  const statusTotal = statusDistribution.reduce((sum, s) => sum + (s.count || 0), 0);
  const paymentTotal = paymentMethodStats.reduce((sum, p) => sum + (p.count || 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
            Welcome back{firstName ? <>, <span className="text-gray-400">{firstName}</span></> : ''}
          </h1>
          <p className="text-gray-500 mt-1.5">Here's how Bookwie is doing over the last {selectedPeriod === '365' ? 'year' : `${selectedPeriod} days`}.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-white rounded-full p-1 shadow-card" role="tablist" aria-label="Period">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                role="tab"
                aria-selected={selectedPeriod === p.value}
                onClick={() => setSelectedPeriod(p.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedPeriod === p.value ? 'bg-brand-600 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            className="w-11 h-11 rounded-full bg-white shadow-card text-gray-700 flex items-center justify-center hover:text-brand-700 disabled:opacity-50"
            aria-label="Refresh"
            title="Refresh"
          >
            <FiRefreshCw size={17} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={exportData}
            className="flex items-center gap-2 h-11 px-5 rounded-full bg-white shadow-card text-sm font-medium text-gray-900 hover:text-brand-700"
          >
            <FiDownload size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Row 1: revenue hero, monthly revenue, trend */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-3 flex flex-col gap-5">
          <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-card bg-gradient-to-br from-brand-700 via-brand-600 to-brand-400">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
            <div className="absolute -right-4 bottom-[-60px] w-32 h-32 rounded-full bg-white/10" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="text-sm text-white/80">Revenue</p>
                <GrowthPill growth={revenueStats.revenueGrowth} light />
              </div>
              <p className="text-3xl font-semibold mt-6 tracking-tight">{formatCurrency(revenueStats.totalRevenue)}</p>
              <p className="text-xs text-white/70 mt-1">Last {selectedPeriod} days</p>
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/20 text-sm">
                <span className="text-white/80">Avg. booking</span>
                <span className="font-medium">{formatCurrency(revenueStats.avgBookingValue)}</span>
              </div>
            </div>
          </div>

          <Card>
            <p className="text-xs text-gray-500">Bookings</p>
            <div className="flex items-end justify-between mt-1">
              <p className="text-3xl font-semibold text-gray-900">{formatNumber(bookingStats.total)}</p>
              <span className="text-xs font-medium rounded-full px-2.5 py-1 bg-green-50 text-green-700">
                {percent(bookingStats.completed, bookingStats.total)}% completed
              </span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden flex">
              <div className="bg-green-500" style={{ width: `${percent(bookingStats.completed, bookingStats.total)}%` }} />
              <div className="bg-brand-400" style={{ width: `${percent(bookingStats.confirmed, bookingStats.total)}%` }} />
              <div className="bg-amber-400" style={{ width: `${percent(bookingStats.pending, bookingStats.total)}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-600">
              <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-green-500" />Completed {bookingStats.completed}</span>
              <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-brand-400" />Confirmed {bookingStats.confirmed}</span>
              <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-amber-400" />Pending {bookingStats.pending}</span>
            </div>
          </Card>
        </div>

        <Card className="lg:col-span-6">
          <CardHeader icon={FiCreditCard} title="Monthly Revenue" subtitle="Highest month highlighted" to="/wallet/finance" />
          {monthlyRevenue.length ? (
            <ResponsiveContainer width="100%" height={290}>
              <BarChart data={monthlyRevenue} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                <defs>
                  <pattern id="barHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                    <rect width="8" height="8" fill={BRAND[200]} />
                    <line x1="0" y1="0" x2="0" y2="8" stroke={BRAND[300]} strokeWidth="3" />
                  </pattern>
                  <pattern id="barHatchPeak" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                    <rect width="8" height="8" fill={BRAND[600]} />
                    <line x1="0" y1="0" x2="0" y2="8" stroke={BRAND[800]} strokeWidth="3" />
                  </pattern>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke={NEUTRAL[200]} vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} axisLine={false} tickLine={false} fontSize={12} stroke={NEUTRAL[500]} />
                <YAxis tickFormatter={formatCompactCurrency} axisLine={false} tickLine={false} fontSize={12} stroke={NEUTRAL[500]} />
                <Tooltip
                  cursor={{ fill: BRAND[100], radius: 16 }}
                  formatter={(value) => [formatCurrency(value), 'Revenue']}
                  labelFormatter={monthLabel}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="revenue" radius={[999, 999, 999, 999]} maxBarSize={56}>
                  {monthlyRevenue.map((m, i) => (
                    <Cell key={i} fill={m.revenue === peakMonthRevenue && peakMonthRevenue > 0 ? 'url(#barHatchPeak)' : 'url(#barHatch)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart height={290} />}
        </Card>

        <Card className="lg:col-span-3 flex flex-col !px-0 !pb-0 overflow-hidden">
          <div className="px-5">
            <CardHeader title="Revenue Trend" subtitle="Daily, selected period" />
            <p className="text-xs text-gray-500 text-center">All-time revenue</p>
            <p className="text-3xl font-semibold text-gray-900 text-center mt-1 tracking-tight">{formatCurrency(totalStats.totalRevenue)}</p>
          </div>
          <div className="flex-1 min-h-[160px] mt-4">
            {bookingTrends.length ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                <AreaChart data={bookingTrends} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BRAND[400]} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={BRAND[400]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" hide />
                  <YAxis hide />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                    labelFormatter={shortDate}
                    contentStyle={tooltipStyle}
                  />
                  <Area type="monotone" dataKey="revenue" stroke={BRAND[600]} strokeWidth={2} fill="url(#trendFill)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart height={160} />}
          </div>
        </Card>
      </div>

      {/* Row 2: quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatTile icon={FiUsers} label="Customers" value={formatNumber(totalStats.totalUsers)} hint="Registered users" />
        <StatTile icon={FiBriefcase} label="Vendors" value={formatNumber(vendorStats.total)} hint={`${vendorStats.approved} approved · ${vendorStats.pending} pending`} />
        <StatTile icon={FiCalendar} label="All-time bookings" value={formatNumber(totalStats.totalBookings)} hint={`${vendorStats.active} active vendors`} />
        <StatTile icon={FiCheckCircle} label="Completion rate" value={`${percent(bookingStats.completed, bookingStats.total)}%`} hint="Of bookings this period" />
      </div>

      {/* Row 3: recent bookings + status */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-8">
          <CardHeader title="Recent Bookings" subtitle="Latest activity across all salons" to="/appointments" />
          {recentBookings.length ? (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-xs text-gray-500">
                    <th className="text-left font-medium px-3 pb-2">Customer</th>
                    <th className="text-left font-medium px-3 pb-2">Salon</th>
                    <th className="text-left font-medium px-3 pb-2">Slot</th>
                    <th className="text-left font-medium px-3 pb-2">Status</th>
                    <th className="text-right font-medium px-3 pb-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.slice(0, 6).map((booking) => {
                    const s = statusStyle(booking.status);
                    const customer = booking.userId?.name || 'Unknown user';
                    return (
                      <tr key={booking._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-3 rounded-l-2xl">
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-brand-100 text-brand-800 text-xs font-semibold flex items-center justify-center shrink-0">
                              {customer.charAt(0).toUpperCase()}
                            </span>
                            <span className="font-medium text-gray-900 truncate">{customer}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-gray-600 max-w-[180px] truncate">
                          {booking.vendorId?.store_name || booking.vendorId?.name || 'Unknown salon'}
                        </td>
                        <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{booking.bookingDate} · {booking.timeSlot}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium capitalize ${s.badge}`}>
                            <i className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-gray-900 rounded-r-2xl whitespace-nowrap">
                          {formatCurrency(booking.totalAmount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-10 text-center">No bookings yet</p>
          )}
        </Card>

        <Card className="lg:col-span-4">
          <CardHeader title="Booking Status" subtitle="Share of bookings this period" />
          {statusTotal ? (
            <>
              <div className="relative">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={statusDistribution} dataKey="count" nameKey="status" innerRadius={62} outerRadius={88} paddingAngle={3} cornerRadius={8} stroke="none">
                      {statusDistribution.map((s) => (
                        <Cell key={s.status} fill={statusStyle(s.status).color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="text-2xl font-semibold text-gray-900">{formatNumber(statusTotal)}</p>
                  <p className="text-xs text-gray-500">bookings</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {statusDistribution.map((s) => (
                  <div key={s.status} className="flex items-center justify-between rounded-2xl bg-gray-50 px-3 py-2 text-xs">
                    <span className="flex items-center gap-2 capitalize text-gray-600">
                      <i className={`w-2 h-2 rounded-full ${statusStyle(s.status).dot}`} />{s.status}
                    </span>
                    <span className="font-semibold text-gray-900">{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <EmptyChart height={200} />}
        </Card>
      </div>

      {/* Row 4: top vendors, bookings trend, payment methods */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-4">
          <CardHeader title="Top Vendors" subtitle="By revenue this period" to="/vendors-list" />
          {topVendors.length ? (
            <ul className="space-y-1">
              {topVendors.slice(0, 5).map((vendor, index) => (
                <li key={index} className="flex items-center gap-3 rounded-2xl px-2 py-2.5 hover:bg-gray-50">
                  <span className="w-6 text-xs font-semibold text-gray-400 text-center">{index + 1}</span>
                  <span className="w-9 h-9 rounded-full bg-brand-100 text-brand-800 text-sm font-semibold flex items-center justify-center shrink-0">
                    {vendor.vendorName?.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{vendor.vendorName}</p>
                    <p className="text-xs text-gray-500">{vendor.totalBookings} bookings · avg {formatCurrency(vendor.avgBookingValue)}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{formatCurrency(vendor.totalRevenue)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 py-10 text-center">No vendor revenue yet</p>
          )}
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader title="Daily Bookings" subtitle={`Last ${selectedPeriod} days`} />
          {bookingTrends.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={bookingTrends} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="bookingsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={NEUTRAL[900]} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={NEUTRAL[900]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke={NEUTRAL[200]} vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} axisLine={false} tickLine={false} fontSize={12} stroke={NEUTRAL[500]} minTickGap={24} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} fontSize={12} stroke={NEUTRAL[500]} />
                <Tooltip formatter={(value) => [value, 'Bookings']} labelFormatter={shortDate} contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="bookings" stroke={NEUTRAL[900]} strokeWidth={2} fill="url(#bookingsFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart height={240} />}
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader title="Payment Methods" subtitle="Online vs. pay at salon" />
          {paymentTotal ? (
            <div className="space-y-4 mt-2">
              {paymentMethodStats.map((p, i) => (
                <div key={p._id || i}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="capitalize text-gray-700">{p._id || 'Unknown'}</span>
                    <span className="font-semibold text-gray-900">{percent(p.count, paymentTotal)}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${percent(p.count, paymentTotal)}%`, backgroundColor: i === 0 ? BRAND[600] : BRAND[300] }} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatNumber(p.count)} bookings</p>
                </div>
              ))}
            </div>
          ) : <EmptyChart height={160} />}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
